import remote from '../remote'

const state = {
  invoiceData: [],
  invoices: [],
  subInvoices: [],
  subInvoiceSeries: [],
  finishedOrders: [],
  archivedInvoices: []
}

const getters = {
  FINISHED_ORDERS: state => {
    return state.finishedOrders
  },
  FINISHED_ORDERS_BY_TYPE: (state, getters) => (type, connected = false) => {
    return state.finishedOrders.filter(order => {
      if (connected === false) {
        return (order.type === type) && !order.data.connectedOrder
      }
      return order.data.connectedOrder || order.type === type
    })
  },
  ARCHIVED_INVOICES: state => {
    return state.archivedInvoices.reverse()
  },
  INVOICES: state => {
    return state.invoices
  },
  INVOICES_BY_TYPE: (state, getters) => (type) => {
    return state.invoices.filter(invoice => invoice.type === type)
  },
  INVOICES_NEW_BY_TYPE: (state, getters) => (type) => {
    return state.invoices.filter(invoice => invoice.type === type && (invoice.state !== 'fakturerad' && invoice.state !== 'arkiverad'))
  },
  INVOICE_BY_ID: (state, getters) => (id) => {
    return state.invoices.find(invoice => invoice.id === id)
  },
  SUB_INVOICES: state => {
    return state.subInvoices.reverse()
  },
  SUB_INVOICE_BY_ID: (state, getters) => (id) => {
    return state.subInvoices.find(invoice => invoice.id === id)
  },
  SUB_INVOICE_SERIES: state => {
    return state.subInvoiceSeries
  },
  SUB_INVOICE_SERIE_FOR_ID: (state, getters) => (id) => {
    for (let i = 0; i < state.subInvoiceSeries.length; i++) {
      const next = i + 1

      if (state.subInvoiceSeries.length === next && state.subInvoiceSeries[i].reset < id) {
        return state.subInvoiceSeries[i]
      }

      if (state.subInvoiceSeries[i].reset < id && state.subInvoiceSeries[next].reset >= id) {
        return state.subInvoiceSeries[i]
      }
    }
  }
}

const mutations = {
  SET_FINISHED_ORDERS: (state, payload) => {
    state.finishedOrders = payload
  },
  REMOVE_FINISHED_ORDER: (state, payload) => {
    const i = state.finishedOrders.map(item => item.id).indexOf(payload)
    state.finishedOrders.splice(i, 1)
  },
  SET_INVOICES: (state, payload) => {
    state.invoices = payload
  },
  SET_INVOICE: (state, invoice) => {
    state.invoices = [
      ...state.invoices.map(item => item.id !== invoice.id ? item : { ...item, ...invoice })
    ]
  },
  SET_MODIFY_INVOICE: (state, invoice) => {
    const i = state.invoices.findIndex(v => v.id === invoice.id)
    if (i === -1) {
      state.invoices.push(invoice)
    } else {
      state.invoices.splice(i, 1, invoice)
    }
  },
  SET_SUB_INVOICES: (state, payload) => {
    state.subInvoices = payload
  },
  SET_ARCHIVED_INVOICES: (state, payload) => {
    state.archivedInvoices = payload
  },
  SET_SUB_INVOICE_SERIES: (state, payload) => {
    state.subInvoiceSeries = payload
  }
}

const actions = {
  GET_FINISHED_ORDERS: async (context, payload) => {
    let { data } = await remote().get('/api/orders/by-state/fakturaredo')
    context.commit('SET_FINISHED_ORDERS', data)
  },
  CHANGE_ORDER_STATE: async (context, payload) => {
    await remote().put('/api/orders/' + payload.id + '/changestate/' + payload.state)
    context.commit('REMOVE_FINISHED_ORDER', payload.id)
  },
  GET_ACTIVE_INVOICES: async (context, payload) => {
    let { data } = await remote().get('/api/invoices/invoice')
    context.commit('SET_INVOICES', data)
  },
  GET_INVOICE_ID: async (context, payload) => {
    const { data } = await remote().get('/api/invoices/invoice/' + payload.id)
    context.commit('SET_MODIFY_INVOICE', data)
  },
  GET_SUB_INVOICES: async (context, payload) => {
    const { data } = await remote().get('/api/invoices/subInvoice')
    context.commit('SET_SUB_INVOICES', data)
  },
  GET_SUB_INVOICE_SERIES: async (context, payload) => {
    const { data } = await remote().get('/api/invoices/subInvoiceSerie')
    context.commit('SET_SUB_INVOICE_SERIES', data)
  },
  GET_ARCHIVED_INVOICES: async (context, payload) => {
    const { data } = await remote().get('/api/invoices/by-state/arkiverad')
    context.commit('SET_ARCHIVED_INVOICES', data)
  },
  ADD_ORDERS_TO_INVOICE: async (context, payload) => {
    for (const order of payload.orders) {
      await remote().post('/api/invoices/invoiceData', {
        invoiceData: {
          orderId: order.id,
          collectionId: payload.invoice
        }
      })
    }
  },
  ADD_ORDERS_TO_NEW_INVOICE: async (context, payload) => {
    let { data } = await remote().post('/api/invoices/invoice', {
      invoice: {
        type: payload.invoiceType,
        name: payload.invoiceName,
        state: 'new'
      }
    })
    await actions.ADD_ORDERS_TO_INVOICE(context, {
      invoice: data.id,
      orders: payload.orders
    })
  },
  REMOVE_ORDERS_FROM_INVOICE: async (context, payload) => {
    for (const order of payload.orders) {
      await remote().delete('/api/invoices/invoiceData/' + order.id)
    }
  },
  DELETE_INVOICE: async (context, payload) => {
    await remote().delete('/api/invoices/invoice/' + payload)
  },
  ARCHIVE_INVOICE: async (context, payload) => {
    const { data } = await remote().patch('/api/invoices/invoice/' + payload, { invoice: { state: 'arkiverad' } })
    context.commit('SET_INVOICE', data)
  },
  UPDATE_INVOICE_DATA: async (context, payload) => {
    const { data } = await remote().patch('/api/invoices/invoice/' + payload.id, { invoice: { data: payload.data } })
    context.commit('SET_INVOICE', data)
  },
  SAVE_INVOICE_DATA: async (context, payload) => {
    const { data } = await remote().patch('/api/invoices/invoice/' + payload.id, { invoice: { data: payload.data, state: 'saved' } })
    context.commit('SET_INVOICE', data)
  },
  ADD_SUB_INVOICE: async (context, payload) => {
    const { data } = await remote().post('/api/invoices/subInvoice', {
      subInvoice: {
        ...payload
      }
    })
    return data
  },
  UPDATE_SUB_INVOICE: async (context, payload) => {
    const { data } = await remote().patch('/api/invoices/subInvoice/' + payload.id, {
      subInvoice: {
        ...payload
      }
    })
    return data
  }
}

export default {
  state, getters, mutations, actions
}



// WEBPACK FOOTER //
// ./src/modules/invoice.js